import { useState, FC, useEffect, useCallback, useMemo } from 'react'
import { AxiosError } from 'axios'
import {
    FirmViewSectionContainer,
    FlexDiv,
    OrangeButton,
    OrangeButtonSimpleText,
    FirmAdministratorBadge,
    OrangeButtonOutlined
} from '../index'
import { IoIosCheckmarkCircle } from 'react-icons/io'
import { MdDeleteOutline } from 'react-icons/md'
import StandardInput from 'src/components/basic/StandardInput'
import StandardCheckbox from 'src/components/basic/StandardCheckbox'
import { FirmDetailsContactsDetailedData, FirmDetailedContactData } from 'src/dtos/portal'
import { api } from 'src/services/apiClient'
import Modal from 'react-modal'
import { object, string } from 'yup'
import { useAuth } from 'src/context/auth'
import { useToast } from 'src/hooks/useToast'
import { appendErrorSource } from 'src/utils/serverError'

interface FirmViewContactInformationProps {
    firmId: string;
    isAdmin: boolean
}

interface ContactError {
    errors: {
        [key: string]: string[];
    };
}

const FirmViewContactInformation: FC<FirmViewContactInformationProps> = ({
    firmId,
    isAdmin
}) => {
    const { user } = useAuth()
    const { callToast } = useToast()
    const [editFirstSection, setEditFirstSection] = useState(false)
    const [editSecondSection, setEditSecondSection] = useState(false)
    const [primaryContact, setPrimaryContact] = useState<FirmDetailedContactData | undefined>(undefined)
    const [secondaryContact, setSecondaryContact] = useState<FirmDetailedContactData | undefined>(undefined)
    const [originalContacts, setOriginalContacts] = useState<FirmDetailsContactsDetailedData | undefined>(undefined)
    const [removeContactModalOpen, setRemoveContactModalOpen] = useState(false)
    const [actionButtonDisabled, setActionButtonDisabled] = useState(false)

    const fetchContactsDetailed = useCallback(async () => {
        if(firmId) {
            try {
                const { data } = await api.get('Firm/FirmContactsDetailed', {
                    params: {
                        firmId
                    }
                })

                setOriginalContacts(data)
                const [primary, secondary] = data.contacts
                setPrimaryContact(primary)
                setSecondaryContact(secondary)
            } catch(e) {
                setOriginalContacts(undefined)
                setPrimaryContact(undefined)
                setSecondaryContact(undefined)
            }
        } else {
            setOriginalContacts(undefined)
            setPrimaryContact(undefined)
            setSecondaryContact(undefined)
        }
    }, [firmId])

    const showErrorToast = useCallback((message = 'An error occurred. Please try again later.') => {
        callToast({
            header: 'Unexpected Error',
            footer: message,
            type: 'error'
        })
    }, [callToast])

    const updateFirmContacts = useCallback(async () => {
        setActionButtonDisabled(true)
        try {
            await api.patch('/Firm/UpdateFirmContacts', {
                firmId,
                contacts: [
                    primaryContact,
                    secondaryContact
                ]
            })
            await fetchContactsDetailed()
        } catch(error) {
            throw error
        } finally {
            setActionButtonDisabled(false)
        }
    }, [fetchContactsDetailed, firmId, primaryContact, secondaryContact])

    const showContactError = useCallback((contactError: ContactError) => {
        const firstError = Object.values(contactError.errors)[0]
        const contactErrorMessage = firstError[0]
        showErrorToast(contactErrorMessage)
    }, [showErrorToast])
    
    const updateFirstContact = async () => {
        try {
            await updateFirmContacts()
            setEditFirstSection(false)
        } catch(error) {
            handleBackendError(error)
        }
    }

    const updateSecondaryContact = async () => {
        try {
            await updateFirmContacts()
            setEditSecondSection(false)
        } catch(error) {
            handleBackendError(error)
        }
    }

    const handleBackendError = (error: any) => {
        const axiosError = error as AxiosError
        if(axiosError.response?.data) {
            const errorData = axiosError.response?.data as any

            // Log actual error details to browser console
            if(errorData.errorDetails) {
                console.error(errorData.errorDetails)
            }

            if(errorData.errorMessage) {
                showErrorToast(appendErrorSource(errorData.errorMessage, errorData))
                return
            }
            const contactError = errorData as ContactError
            if(contactError.errors) {
                showContactError(contactError)
                return
            }
        }
        showErrorToast()
    }


    const handleRemoveSecondaryContact = async () => {
        setActionButtonDisabled(true)
        try {
            await api.patch('/Firm/RemoveSecondaryFirmContact', {
                firmId,
                firmContactId: secondaryContact?.id
            })
            await fetchContactsDetailed()
            setRemoveContactModalOpen(false)
        } catch(e) {} finally {
            setActionButtonDisabled(false)
        }
    }
    
    useEffect(() => {
        fetchContactsDetailed()
    }, [fetchContactsDetailed])

    const handleEditInput = (isPrimary: boolean, key: keyof FirmDetailedContactData, value: any) => {
        if(isPrimary) {
            setPrimaryContact(prev => ({ ...prev, [key]: value }))
        } else {
            setSecondaryContact(prev => ({ ...prev, [key]: value }))
        }
    }

    const [originalPrimary, originalSecondary] = originalContacts?.contacts ?? []

    const validationSchema = object({
        firstName: string().required(),
        lastName: string().required(),
        emailAddress: string().email().required(),
    })

    const isFormValid = useMemo(() => {
        try {
            validationSchema.validateSync(primaryContact)
            return true
        } catch (e) {
            return false
        }
    }, [primaryContact, validationSchema])

    return (
        <FirmViewSectionContainer>
            <h1 style={{ color: user.role === 'Client' ? '#937251' : '#3D6666' }}>Contact Information</h1>
            <FlexDiv dir='column' margin='32px 0 0'>
                <FlexDiv gap="24px" align="center">
                    <h4>PRIMARY</h4>
                    {originalPrimary?.isPrimary === true ? (
                        <FirmAdministratorBadge>
                            <IoIosCheckmarkCircle/>
                            <span>Firm Administrator</span>
                        </FirmAdministratorBadge>
                    ) : null}
                </FlexDiv>
                <hr style={{ marginTop: '16px' }} />
                <FlexDiv className='contact-item' justify='space-between' align='flex-start' margin="24px 0 0">
                    <FlexDiv dir="column" gap="42px">
                        <table>
                            <thead>
                                <tr>
                                    <th>First Name</th>
                                    <th>Last Name</th>
                                    <th>Email</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        {editFirstSection ? (
                                            <FlexDiv maxWidth='200px'>
                                                <StandardInput
                                                    label=""
                                                    value={primaryContact?.firstName ?? ''}
                                                    onChange={e => handleEditInput(true, 'firstName', e.target.value)}
                                                />
                                            </FlexDiv>
                                        ) : originalPrimary?.firstName ?? ''}
                                    </td>
                                    <td>{editFirstSection ? (
                                        <FlexDiv maxWidth='200px'>
                                            <StandardInput
                                                label=""
                                                value={primaryContact?.lastName ?? ''}
                                                onChange={e => handleEditInput(true, 'lastName', e.target.value)}
                                            />
                                        </FlexDiv>
                                    ) : originalPrimary?.lastName ?? ''}</td>
                                    <td>
                                        {editFirstSection ? (
                                            <FlexDiv maxWidth='200px'>
                                                <StandardInput
                                                    label=""
                                                    value={primaryContact?.emailAddress ?? ''}
                                                    onChange={e => handleEditInput(true, 'emailAddress', e.target.value)}
                                                />
                                            </FlexDiv>
                                        ) : originalPrimary?.emailAddress ?? ''}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                       {(!isAdmin)? 
                       <>
                            <p>
                                If you need to change your primary firm contact information during your policy period, 
                                please email member<br /> services at {" "}
                                <a href="info@tlie.org">
                                info@tlie.org
                                </a>
                                {" "} with any requested changes.  
                            </p>
                            <p>
                                If you need further assistance, you can contact member services at 512-480-9074.
                            </p>
                       </>
                       :null
                       }
                        {editFirstSection ? (
                            <StandardCheckbox
                                label="This person is the Firm's Primary Contact"
                                variation='small'
                                onChange={() => {
                                    setPrimaryContact(prev => ({ ...prev, isPrimary: !prev?.isPrimary }))
                                    setSecondaryContact(prev => ({ ...prev, isPrimary: !prev?.isPrimary }))
                                }}
                                checked={primaryContact?.isPrimary}
                            />
                        ) : null}
                    </FlexDiv>
                    {(isAdmin) ? <FlexDiv gap="24px" maxWidth='fit-content' align='center'>
                        {editFirstSection ? (
                            <>
                                <OrangeButtonSimpleText
                                    onClick={() => {
                                        setEditFirstSection(false)
                                        setPrimaryContact(originalContacts?.contacts[0])
                                    }}
                                    disabled={actionButtonDisabled}
                                >
                                    Cancel
                                </OrangeButtonSimpleText>
                                <OrangeButton
                                    onClick={updateFirstContact}
                                    disabled={actionButtonDisabled || !isFormValid}
                                >
                                    Save
                                </OrangeButton>
                            </>
                        ) : (
                            <OrangeButton
                                onClick={() => setEditFirstSection(true)}
                                disabled={editSecondSection}
                            >
                                Edit
                            </OrangeButton>
                        )}
                    </FlexDiv> : null}
                </FlexDiv>
            </FlexDiv>
            {(originalSecondary || editSecondSection) ? (
                <FlexDiv dir='column' margin='64px 0 0' className='contact-item'>
                    <FlexDiv gap="24px" align="center">
                        <h4>SECONDARY</h4>
                        {originalSecondary?.isPrimary === true ? (
                            <FirmAdministratorBadge>
                                <IoIosCheckmarkCircle/>
                                <span>Firm Administrator</span>
                            </FirmAdministratorBadge>
                        ) : null}
                    </FlexDiv>
                    <hr style={{ marginTop: '16px' }} />
                    <FlexDiv justify='space-between' align='flex-start' margin={`24px 0 ${editSecondSection ? '32px' : 0}`}>
                        <FlexDiv dir='column' gap="42px">
                            <table>
                                <thead>
                                    <tr>
                                        <th>First Name</th>
                                        <th>Last Name</th>
                                        <th>Email</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            {editSecondSection ? (
                                                <FlexDiv maxWidth='200px'>
                                                    <StandardInput
                                                        label=""
                                                        value={secondaryContact?.firstName ?? ''}
                                                        onChange={e => handleEditInput(false, 'firstName', e.target.value)}
                                                    />
                                                </FlexDiv>
                                            ) : originalSecondary?.firstName ?? 'N/A'}
                                        </td>
                                        <td>
                                            {editSecondSection ? (
                                                <FlexDiv maxWidth='200px'>
                                                    <StandardInput
                                                        label=""
                                                        value={secondaryContact?.lastName ?? ''}
                                                        onChange={e => handleEditInput(false, 'lastName', e.target.value)}
                                                    />
                                                </FlexDiv>
                                            ) : originalSecondary?.lastName ?? 'N/A'}
                                        </td>
                                        <td>
                                            {editSecondSection ? (
                                                <FlexDiv maxWidth='200px'>
                                                    <StandardInput
                                                        label=""
                                                        value={secondaryContact?.emailAddress ?? ''}
                                                        onChange={e => handleEditInput(false, 'emailAddress', e.target.value)}
                                                    />
                                                </FlexDiv>
                                            ) : originalSecondary?.emailAddress ?? 'N/A'}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            {editSecondSection ? (
                                <StandardCheckbox
                                    label="This person is the Firm's Primary Contact"
                                    variation='small'
                                    onChange={() => {
                                        setSecondaryContact(prev => ({ ...prev, isPrimary: !prev?.isPrimary }))
                                        setPrimaryContact(prev => ({ ...prev, isPrimary: !prev?.isPrimary }))
                                    }}
                                    checked={secondaryContact?.isPrimary}
                                />
                            ) : null}
                        </FlexDiv>
                        {(isAdmin) ? 
                        <FlexDiv gap="24px" maxWidth='fit-content' align='center'>
                        {editSecondSection ? (
                            <>
                                <OrangeButtonSimpleText
                                    onClick={() => {
                                        setSecondaryContact(originalContacts?.contacts[1])
                                        setEditSecondSection(false)
                                    }}
                                    disabled={actionButtonDisabled}
                                >
                                    Cancel
                                </OrangeButtonSimpleText>
                                <OrangeButton
                                    onClick={updateSecondaryContact}
                                    disabled={actionButtonDisabled}
                                >
                                    Save
                                </OrangeButton>
                            </>
                        ) : (
                            <>
                                <OrangeButtonSimpleText
                                    onClick={() => setRemoveContactModalOpen(true)}
                                >
                                    <MdDeleteOutline/>
                                    Remove
                                </OrangeButtonSimpleText>
                                <OrangeButton
                                    onClick={() => setEditSecondSection(true)}
                                    disabled={editFirstSection}
                                >
                                    Edit
                                </OrangeButton>
                            </>
                        )}
                    </FlexDiv> : null
                        }
                    </FlexDiv>
                </FlexDiv>
            ) : ((isAdmin) ?
                <FlexDiv margin="64px 0 0 0">
                    <OrangeButtonOutlined
                        onClick={() => {
                            setSecondaryContact({})
                            setEditSecondSection(true)
                        }}
                        style={{ padding: '8px 48px' }}
                    >
                        + Add a Secondary Contact
                    </OrangeButtonOutlined>
                </FlexDiv>
                : null 
                //null
            )}
            <Modal
                isOpen={removeContactModalOpen}
                onRequestClose={() => setRemoveContactModalOpen(false)}
                overlayClassName="react-modal-overlay"
                className="react-modal-content"
            >
                <FlexDiv dir='column' gap="16px">
                    <strong>Are you sure you want to remove this secondary contact?</strong>
                    <span>This information will not be saved once it has been removed.</span>
                    <FlexDiv dir="column" gap="24px" margin="48px 0 0 0" align='flex-end'>
                        <OrangeButton
                            onClick={handleRemoveSecondaryContact}
                            disabled={actionButtonDisabled}
                        >
                            Remove Secondary Contact
                        </OrangeButton>
                        <OrangeButtonSimpleText
                            onClick={() => setRemoveContactModalOpen(false)}
                            disabled={actionButtonDisabled}
                        >
                            Cancel
                        </OrangeButtonSimpleText>
                    </FlexDiv>
                </FlexDiv>
            </Modal>
        </FirmViewSectionContainer>
    )
}

export default FirmViewContactInformation